import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddButtonInTopBar from "../components/AddButtonInTopBar";
import DataGrid from "../components/common/DataGrid";
import PageContent from "../components/pageContent";
import ProductBreadCrumb from "../components/products/ProductBreadCrumb";
import TopBarWithBreadCrumb from "../components/TopBarWithBreadCrumb";
import { checkCrud } from "../helpers/userRolesHelper";
import ReceiptPopup from "../popups/ReceiptPopup";
import { addNotification } from "../redux/actions";
import { apiDeleteReceipt, apiGetReceiptById, apiGetReceipts } from "../services/ReceiptsService";
import { useTranslation } from "react-i18next";

interface Props {
    contextLevel: string;
}

interface PopupReceiptDetail {
    visible: boolean,
    receiptDetail: any,
    id: any
}

const entityName = 'receipt';

const RecepturyPage = (props: Props) => {

    const { t } = useTranslation(undefined, { keyPrefix: 'Receipts' });

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ rows: [] });

    let initReceiptDetail: PopupReceiptDetail = {
        visible: false,
        receiptDetail: null,
        id: undefined,
    }

    const [receiptPopupData, setReceiptPopupData] = useState(initReceiptDetail);

    const productInfo = useSelector((state: any) => state.productInfo);
    const dispatch = useDispatch();

    const [canCreate, setCanCreate] = useState(false);
    const [canRead, setCanRead] = useState(false);
    const [canUpdate, setCanUpdate] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    const userInfo = useSelector((state: any) => state.userInfo);

    const setRoles = () => {
        setCanCreate(checkCrud(entityName, 'create', userInfo));
        setCanRead(checkCrud(entityName, 'read', userInfo));
        setCanUpdate(checkCrud(entityName, 'update', userInfo));
        setCanDelete(checkCrud(entityName, 'delete', userInfo));
    }

    useEffect(() => {
        setRoles();
    }, [userInfo.UserRoles])

    useEffect(() => {
        loadData();
    }, [productInfo]);

    const loadData = () => {

        if (productInfo) {
            apiGetReceipts(productInfo.productId).then((responseData) => {
                setData({
                    rows: responseData.data,
                });

                setLoading(false);
            });
        }
    };

    const handleDeleteRow = (row) => {
        apiDeleteReceipt(row.id)
            .then((responseData) => {
                dispatch(addNotification({ text: t('ReceiptHasBeenRemoved'), type: 'success' }));

                loadData();
                setLoading(false);
            })
            .catch((err: any) => {
                dispatch(addNotification({ text: `${t('ReceiptCouldNotBeRemoved')} ${err.response.data}`, type: 'error' }));
            });
    }


    return (
        <>
            {receiptPopupData.visible && (
                <ReceiptPopup
                    isCustom={false}
                    id={receiptPopupData.id}
                    productPrefersLitres={productInfo.productPrefersLitres}
                    receiptDetail={receiptPopupData.receiptDetail}
                    refreshData={() => {
                        loadData();
                        setReceiptPopupData({ visible: false, id: undefined, receiptDetail: null });
                    }
                    }
                    handleCancel={() => {
                        setReceiptPopupData({ visible: false, id: undefined, receiptDetail: null });
                    }}
                />
            )}

            <PageContent>
                <>
                    <ProductBreadCrumb selectionOfProductIsRequired={true} />
                    <TopBarWithBreadCrumb
                        contextLevel={props.contextLevel}
                        pageLevel={t('Label')}
                        rightAreaComponent={
                            <AddButtonInTopBar
                                enabled={canCreate}
                                setPopupData={setReceiptPopupData}
                                text={t('CreateText')}
                            />
                        }
                    />

                    <DataGrid
                        key={`RecepturyGrid_Product_${productInfo?.productId}`}
                        loading={loading}
                        rows={data.rows}
                        showDeleteButton={canDelete}
                        showEditButton={canUpdate}
                        handleEditAction={async (id: any) => {

                            let receiptDetail = await apiGetReceiptById(id);
                            setReceiptPopupData({ visible: true, id: id, receiptDetail: receiptDetail.data });
                        }}
                        onDeleteRow={handleDeleteRow}
                        cols={[
                            { name: t('Fields.product'), dataField: "productName" },
                            { name: t('Fields.basis'), dataField: "basisName", sortable: true },
                            { name: productInfo.productPrefersLitres ? t('Fields.basicAmountInMiliGrams') : t('Fields.basicAmountInGrams'), dataField: "customAmount", sortable: true },
                            { name: t('Fields.sampleCard'), dataField: "sampleCardName", sortable: true, filterable: true, filterType: "text" },
                            { name: t('Fields.color'), dataField: "colorName", sortable: true, filterable: true, filterType: "text" },
                            { name: t('Fields.version'), dataField: "version", filterable: true },
                            { name: t('Fields.special'), dataField: "sellerName" },
                            { name: t('Fields.pigmentsRatio'), dataField: "pigmentsRatio" },
                            { name: t('Fields.noteInternal'), dataField: "noteInternal" },
                            { name: t('Fields.noteExternal'), dataField: "noteExternal" }
                        ]}
                    />
                </>
            </PageContent>
        </>
    );
};

export default RecepturyPage;
